"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

const FORMATS = [
  { value: "jpg", label: "JPG" },
  { value: "png", label: "PNG" },
  { value: "webp", label: "WEBP" },
];

const INPUT_FORMATS = [
  ...FORMATS,
  { value: "gif", label: "GIF" },
  { value: "bmp", label: "BMP" },
  { value: "tiff", label: "TIFF" },
  { value: "svg", label: "SVG" }, // Browser support varies for reading
];

// ... imports

interface ImageConverterProps {
  defaultSource?: string;
  defaultTarget?: string;
}

export function ImageConverter({ defaultSource = "jpg", defaultTarget = "png" }: ImageConverterProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [sourceFormat, setSourceFormat] = useState<string>(defaultSource);
  const [targetFormat, setTargetFormat] = useState<string>(defaultTarget);
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "image-converter") ?? tools[0],
    locale
  );

  // Update state if props change (e.g. navigation)
  useEffect(() => {
    setSourceFormat(defaultSource);
    setTargetFormat(defaultTarget);
  }, [defaultSource, defaultTarget]);

  // Auto-detect source format on file change
  useEffect(() => {
    if (file) {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (extension) {
        // Map common extensions to formats
        if (extension === "jpeg") setSourceFormat("jpg");
        else if (INPUT_FORMATS.some(f => f.value === extension)) setSourceFormat(extension);
        // If unknown, keep the default or current selection
      }
    }
  }, [file]);
// ... rest of component

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const convertImage = () => {
    if (!file) return;

    setIsProcessing(true);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Fill background white for JPG conversion if transparent
      if (targetFormat === "jpg") {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0);

      const outputMimeType = `image/${targetFormat === "jpg" ? "jpeg" : targetFormat}`;
      const quality = targetFormat === "jpg" || targetFormat === "webp" ? 0.9 : undefined; // High quality for lossy formats

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Image conversion failed: no blob returned");
            setIsProcessing(false);
            return;
          }

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `converted-${file.name.split('.')[0]}.${targetFormat}`;
          link.click();
          URL.revokeObjectURL(url);
          setIsProcessing(false);
        },
        outputMimeType,
        quality
      );
    };

    img.src = preview;
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:information-variant" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

       {/* Dropdown Selection Area */}
       <div className="bg-white rounded-[20px] border border-positivus-dark/10 p-6 shadow-sm">
         <div className="flex flex-col md:flex-row items-center gap-4 justify-center">
            {/* Source Format */}
            <div className="w-full md:w-1/3">
              <label className="block text-sm font-medium text-gray-700 mb-2">{ui.from}</label>
              <div className="relative">
                <select 
                  value={sourceFormat}
                  onChange={(e) => setSourceFormat(e.target.value)}
                  disabled={!!file} // Disable if file is uploaded (auto-detected)
                  className="w-full appearance-none rounded-[15px] border-2 border-positivus-dark/20 bg-white px-4 py-3 font-medium text-positivus-dark focus:border-positivus-dark focus:outline-none transition-all disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="unknown" disabled>{ui.autoDetect}</option>
                  {INPUT_FORMATS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                 <Icon icon="mdi:chevron-down" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Arrow Icon */}
            <div className="mt-6 text-positivus-dark/50">
               <Icon icon="mdi:arrow-right" className="h-8 w-8 rotate-90 md:rotate-0" />
            </div>

            {/* Target Format */}
            <div className="w-full md:w-1/3">
              <label className="block text-sm font-medium text-gray-700 mb-2">{ui.to}</label>
              <div className="relative">
                 <select 
                  value={targetFormat}
                  onChange={(e) => setTargetFormat(e.target.value)}
                  className="w-full appearance-none rounded-[15px] border-2 border-positivus-dark bg-white px-4 py-3 font-medium text-positivus-dark focus:ring-4 focus:ring-positivus-green/20 focus:outline-none transition-all cursor-pointer"
                >
                  {FORMATS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <Icon icon="mdi:chevron-down" className="absolute right-4 top-1/2 -translate-y-1/2 text-positivus-dark pointer-events-none" />
              </div>
            </div>
         </div>
       </div>

      {!file ? (
        <FileUploader
          accept="image/*"
          onFilesSelected={(files) => {
            if (files && files[0]) {
               const syntheticEvent = {
                 target: {
                   files: files,
                 }
               } as unknown as React.ChangeEvent<HTMLInputElement>;
               handleFileChange(syntheticEvent);
            }
          }}
          className="p-8 md:p-12 rounded-[30px] bg-white border-dashed border-positivus-dark/20 group relative"
        >
          <div className="absolute inset-0 w-full h-full opacity-0 cursor-pointer pointer-events-none" />
          <div className="flex flex-col items-center">
            <div className="h-20 w-20 rounded-full bg-positivus-green/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
               <Icon icon="mdi:cloud-upload" className="h-10 w-10 text-positivus-dark" />
            </div>
            <div>
              <p className="text-xl font-bold text-positivus-dark mb-2">{ui.dropYourImageHere}</p>
              <p className="text-gray-500">{ui.orClickToBrowse}</p>
            </div>
          </div>
        </FileUploader>
      ) : (
         <div className="space-y-8">
            <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-positivus-green/20 flex items-center justify-center">
                    <Icon icon="mdi:image" className="h-6 w-6 text-positivus-dark" />
                  </div>
                  <div>
                    <p className="font-bold text-lg text-positivus-dark line-clamp-1 max-w-[200px]">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB • {sourceFormat.toUpperCase()}</p>
                  </div>
                </div>
                 <button
                  onClick={() => { setFile(null); setPreview(""); }}
                  className="text-red-500 hover:text-red-700 font-medium text-sm border-2 border-transparent hover:border-red-100 rounded-[10px] px-3 py-1 transition-all ml-auto"
                >
                  {ui.remove}
                </button>
              </div>

               {preview && (
                <div className="rounded-[20px] border-2 border-positivus-dark/10 overflow-hidden bg-gray-50 p-4 mb-6">
                  <div className="aspect-video relative flex items-center justify-center">
                     <img src={preview} alt={ui.preview} className="max-w-full max-h-64 object-contain rounded-[10px]" />
                  </div>
                </div>
              )}

              <button
                onClick={convertImage}
                disabled={isProcessing}
                className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                 {isProcessing ? (
                   <div className="flex items-center justify-center gap-2">
                     <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                     <span>{ui.converting}</span>
                   </div>
                 ) : (
                   <div className="flex items-center justify-center gap-2">
                     <span>{ui.convertTo} {targetFormat.toUpperCase()}</span>
                     <Icon icon="mdi:arrow-right" className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                   </div>
                 )}
              </button>
            </div>
         </div>
      )}
    </div>
  );
}
